import { useCallback, useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";
const API_UNCONFIGURED = API_BASE.includes("your-api-gateway");
const ANALYTICS_RETENTION_DAYS = 365;
const CHART_COLORS = [
  "var(--brand-primary)", "var(--teal)", "var(--green)",
  "var(--amber)", "var(--red)", "var(--blue)", "var(--brand-bright)",
];

// Page ID to display name mapping
const PAGE_LABELS = {
  "home": "Home",
  "comparator": "Permission Comparator",
  "das-mapping": "Data Access Mapping",
  "create-user-group": "Visier Security Manager",
  "compare-access": "Compare Access",
  "entitlement-lookup": "Entitlement Lookup",
  "access-search": "Access Search",
  "access-request-history": "Access Request History",
  "security-access-report": "Security Access Report",
  "servicenow-actions": "ServiceNow Actions",
  "sprint-metrics": "Sprint Metrics",
  "provisioning-errors": "Provisioning Errors",
  "analytics": "Usage & Analytics"
};

function AnalyticsDashboard({ previewMode = false }) {
  const [report, setReport] = useState(previewMode ? { summary: {}, timeline: [], monthlyActivity: [], pageViews: [], topUsers: [] } : null);
  const [loading, setLoading] = useState(!previewMode);
  const [error, setError] = useState("");
  const [dateRange, setDateRange] = useState(30); // Days
  const [refreshing, setRefreshing] = useState(false);
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const usersPerPage = 10;

  const fetchAnalytics = useCallback(async () => {
    if (previewMode) return;
    if (API_UNCONFIGURED) {
      setError("API Gateway is not configured");
      setLoading(false);
      return;
    }

    try {
      setRefreshing(true);
      const now = new Date();
      const endDate = now.toISOString();
      const startDate = dateRange === "ytd"
        ? new Date(now.getFullYear(), 0, 1).toISOString()
        : new Date(now.getTime() - Number(dateRange) * 24 * 60 * 60 * 1000).toISOString();

      // Fetch report
      const reportResponse = await fetch(
        `${API_BASE}/analytics/report?startDate=${startDate}&endDate=${endDate}`
      );
      if (!reportResponse.ok) {
        throw new Error(`Failed to fetch report: ${reportResponse.statusText}`);
      }
      const reportData = await reportResponse.json();
      setReport(reportData);

      setError("");
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange, previewMode]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  if (loading && !report) {
    return (
      <div className="cug-root analytics-root">
        <div className="cug-body analytics-body">
          <div className="analytics-loading">Loading analytics data...</div>
        </div>
      </div>
    );
  }

  if (error && !report) {
    return (
      <div className="cug-root analytics-root">
        <div className="cug-body analytics-body">
          <div className="analytics-error">
            <strong>Error:</strong> {error}
          </div>
        </div>
      </div>
    );
  }

  const summary = report?.summary || {};
  const timeline = report?.timeline || [];
  const monthlyActivity = report?.monthlyActivity || [];
  const isYearToDate = dateRange === "ytd";
  const chartTimeline = isYearToDate
    ? Object.values(timeline.reduce((monthly, point) => {
        const month = point.date.slice(0, 7);
        if (!monthly[month]) monthly[month] = { date: `${month}-01`, events: 0 };
        monthly[month].events += point.events;
        return monthly;
      }, {}))
    : timeline;
  const pageViews = report?.pageViews || [];
  const topUsers = report?.topUsers || [];
  const timelineMaxEvents = Math.max(...chartTimeline.map((point) => point.events), 0);
  const timelineLabelInterval = Math.max(1, Math.ceil(chartTimeline.length / 10));
  const showTimelineValues = chartTimeline.length <= 14;
  const maxMonthlyActiveUsers = Math.max(...monthlyActivity.map((point) => point.activeUsers), 0);

  return (
    <div className="cug-root analytics-root">
      <div className="cug-body analytics-body">
        <div className="analytics-toolbar-card metrics-toolbar-card">
          <div className="analytics-controls">
            <label className="metrics-field">
              <span className="metrics-field-label">Date Range</span>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value === "ytd" ? "ytd" : Number(e.target.value))}
                className="metrics-select"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
                <option value="ytd">Year to date</option>
              </select>
            </label>
            <button
              onClick={fetchAnalytics}
              disabled={refreshing || previewMode}
              className="metrics-secondary-btn"
            >
              {refreshing ? "Refreshing..." : "Refresh Data"}
            </button>
          </div>
        </div>

        {error && (
          <div className="analytics-error-banner">
            {error}
          </div>
        )}
        {previewMode && (
          <div className="analytics-retention-notice">Local profile preview: analytics data is not loaded and no events are sent.</div>
        )}
        {isYearToDate && (
          <div className="analytics-retention-notice">
            Production analytics events are retained for {ANALYTICS_RETENTION_DAYS} days. History before production tracking was enabled is unavailable.
          </div>
        )}

        {/* Summary Cards */}
        <div className="analytics-summary-grid">
        <div className="analytics-summary-card">
          <div className="analytics-summary-value">{summary.totalUniqueUsers || 0}</div>
          <div className="analytics-summary-label">Unique Users</div>
        </div>
        <div className="analytics-summary-card">
          <div className="analytics-summary-value">{summary.totalSessions || 0}</div>
          <div className="analytics-summary-label">Total Sessions</div>
        </div>
        <div className="analytics-summary-card">
          <div className="analytics-summary-value">{summary.totalPageViews || 0}</div>
          <div className="analytics-summary-label">Page Views</div>
        </div>
        <div className="analytics-summary-card">
          <div className="analytics-summary-value">{summary.totalEvents || 0}</div>
          <div className="analytics-summary-label">Total Events</div>
        </div>
        <div className="analytics-summary-card">
          <div className="analytics-summary-value">
            {formatDuration(summary.avgSessionDuration || 0)}
          </div>
          <div className="analytics-summary-label">Avg Session Duration</div>
        </div>
      </div>

        {/* Timeline Chart */}
        <div className="analytics-section metrics-section-card">
          <h2>Activity Timeline</h2>
          <p className="analytics-section-description">
            Total count of all tracked events per {isYearToDate ? "month" : "day"} (page views, button clicks, searches, etc.)
          </p>
          {chartTimeline.length > 0 ? (
            <div className="analytics-timeline-chart">
              <div className={`analytics-chart-container${chartTimeline.length > 30 ? " analytics-chart-container-dense" : ""}`}>
                {chartTimeline.map((point, idx) => {
                  const height = timelineMaxEvents > 0 ? (point.events / timelineMaxEvents) * 100 : 0;
                  const showDateLabel = idx % timelineLabelInterval === 0 || idx === chartTimeline.length - 1;
                  const pointDate = new Date(isYearToDate ? `${point.date.slice(0, 7)}-01T00:00:00` : point.date);
                  return (
                    <div key={idx} className="analytics-chart-bar-wrapper">
                      <div className="analytics-chart-bar-value">
                        {showTimelineValues ? point.events : null}
                      </div>
                      <div
                        className="analytics-chart-bar"
                        style={{ height: `${height}%` }}
                        title={isYearToDate
                          ? `${pointDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}: ${point.events} events`
                          : `${formatDate(point.date)}: ${point.events} events, ${point.uniqueUsers} users`}
                      />
                      <div
                        className="analytics-chart-label"
                        style={{ textAlign: idx === 0 ? "left" : idx === chartTimeline.length - 1 ? "right" : "center" }}
                      >
                        {showDateLabel
                          ? pointDate.toLocaleDateString(undefined, isYearToDate
                              ? { month: "short" }
                              : { month: "short", day: "numeric" })
                          : null}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="analytics-timeline-legend">
                <div className="analytics-legend-item">
                  <div className="analytics-legend-color"></div>
                  <span>{isYearToDate ? "Monthly" : "Daily"} activity (page views, clicks, etc.)</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="analytics-empty-state">
              No activity data available. Data will appear as users interact with the application.
            </div>
          )}
        </div>

        {monthlyActivity.length > 0 && (
          <div className="analytics-section metrics-section-card">
            <h2>Monthly Active Users</h2>
            <p className="analytics-section-description">
              Distinct users with activity in each month within the selected date range.
            </p>
            <div className="analytics-timeline-chart">
              <div className="analytics-chart-container">
                {monthlyActivity.map((point) => {
                  const height = maxMonthlyActiveUsers > 0
                    ? (point.activeUsers / maxMonthlyActiveUsers) * 100
                    : 0;
                  const monthDate = new Date(`${point.month}-01T00:00:00`);
                  const monthLabel = monthDate.toLocaleDateString(undefined, { month: "short", year: "numeric" });
                  return (
                    <div key={point.month} className="analytics-chart-bar-wrapper">
                      <div className="analytics-chart-bar-value">{point.activeUsers}</div>
                      <div
                        className="analytics-chart-bar"
                        style={{ height: `${height}%` }}
                        title={`${monthLabel}: ${point.activeUsers} active users, ${point.sessions} sessions`}
                      />
                      <div className="analytics-chart-label">{monthLabel}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Page Traffic Trends */}
        <div className="analytics-section metrics-section-card">
          <h2>Page Traffic Trends</h2>
          {report?.pageTrends && report.pageTrends.length > 0 ? (
            <div className="analytics-traffic-trend-chart">
              <div className="analytics-trend-container">
                <svg viewBox="0 0 900 300" preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: 'auto' }}>
                  {(() => {
                    const pageTrends = report.pageTrends;
                    const pages = Object.keys(pageTrends[0] || {}).filter(k => k !== 'date');
                    const maxValue = Math.max(...pageTrends.map(trend =>
                      pages.reduce((sum, page) => sum + (trend[page] || 0), 0)
                    ), 1);

                    const width = 900;
                    const height = 300;
                    const padding = { top: 30, right: 60, bottom: 50, left: 60 };
                    const chartWidth = width - padding.left - padding.right;
                    const chartHeight = height - padding.top - padding.bottom;

                    const xStep = chartWidth / Math.max(pageTrends.length - 1, 1);

                    return (
                      <>
                        {/* Y-axis grid lines */}
                        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                          const y = padding.top + chartHeight - (ratio * chartHeight);
                          const value = Math.round(maxValue * ratio);
                          return (
                            <g key={i}>
                              <line
                                x1={padding.left}
                                y1={y}
                                x2={padding.left + chartWidth}
                                y2={y}
                                stroke="var(--border-light)"
                                strokeWidth="1"
                              />
                              <text
                                x={padding.left - 10}
                                y={y + 4}
                                textAnchor="end"
                                fontSize="12"
                                fill="var(--text-secondary)"
                              >
                                {value}
                              </text>
                            </g>
                          );
                        })}

                        {/* Lines for each page */}
                        {pages.map((page, pageIdx) => {
                          const points = pageTrends.map((trend, idx) => {
                            const x = padding.left + (idx * xStep);
                            const value = trend[page] || 0;
                            const y = padding.top + chartHeight - ((value / maxValue) * chartHeight);
                            return `${x},${y}`;
                          }).join(' ');

                          return (
                            <polyline
                              key={pageIdx}
                              points={points}
                              fill="none"
                              stroke={CHART_COLORS[pageIdx % CHART_COLORS.length]}
                              strokeWidth="2"
                            />
                          );
                        })}

                        {/* X-axis labels */}
                        {pageTrends.map((trend, idx) => {
                          if (idx % Math.ceil(pageTrends.length / 8) !== 0) return null;
                          const x = padding.left + (idx * xStep);
                          const date = new Date(trend.date);
                          return (
                            <text
                              key={idx}
                              x={x}
                              y={height - 10}
                              textAnchor="middle"
                              fontSize="11"
                              fill="var(--text-secondary)"
                            >
                              {date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            </text>
                          );
                        })}
                      </>
                    );
                  })()}
                </svg>
              </div>
              <div className="analytics-timeline-legend">
                {Object.keys((report.pageTrends[0] || {})).filter(k => k !== 'date').map((page, idx) => {
                  return (
                    <div key={idx} className="analytics-legend-item">
                      <div className="analytics-legend-color" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}></div>
                      <span>{PAGE_LABELS[page] || page || 'Unknown'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="analytics-empty-state">
              No page trend data available. Data will appear as users navigate through pages.
            </div>
          )}
        </div>

        {/* Page Views */}
        <div className="analytics-section metrics-section-card">
          <h2>Page Views</h2>
          {pageViews.length > 0 ? (
            <div className="analytics-table-container analytics-page-views-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th>Views</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {pageViews.map((item, idx) => {
                    const percentage = summary.totalPageViews > 0
                      ? ((item.views / summary.totalPageViews) * 100).toFixed(1)
                      : 0;
                    return (
                      <tr key={idx}>
                        <td>{PAGE_LABELS[item.page] || item.page || "Unknown"}</td>
                        <td>{item.views}</td>
                        <td>
                          <div className="analytics-percentage-bar-container">
                            <div
                              className="analytics-percentage-bar"
                              style={{ width: `${percentage}%` }}
                            />
                            <span className="analytics-percentage-text">{percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="analytics-empty-state">
              No page view data yet. Navigate to different pages to generate analytics.
            </div>
          )}
        </div>

        {/* Top Users */}
        <div className="analytics-section metrics-section-card">
          <h2>Top Users by Activity</h2>
          {topUsers.length > 0 ? (
            <>
              <div className="analytics-table-container">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Sessions</th>
                      <th>Events</th>
                      <th>Pages Visited</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const startIndex = (currentUserPage - 1) * usersPerPage;
                      const endIndex = startIndex + usersPerPage;
                      const paginatedUsers = topUsers.slice(startIndex, endIndex);
                      return paginatedUsers.map((user, idx) => (
                        <tr key={idx}>
                          <td>{user.email}</td>
                          <td>{user.sessions}</td>
                          <td>{user.events}</td>
                          <td>{user.pagesVisited}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
              {topUsers.length > usersPerPage && (
                <div className="analytics-pagination">
                  <button
                    className="analytics-pagination-btn"
                    onClick={() => setCurrentUserPage(prev => Math.max(1, prev - 1))}
                    disabled={currentUserPage === 1}
                  >
                    ← Previous
                  </button>
                  <span className="analytics-pagination-info">
                    Page {currentUserPage} of {Math.ceil(topUsers.length / usersPerPage)}
                  </span>
                  <button
                    className="analytics-pagination-btn"
                    onClick={() => setCurrentUserPage(prev => Math.min(Math.ceil(topUsers.length / usersPerPage), prev + 1))}
                    disabled={currentUserPage >= Math.ceil(topUsers.length / usersPerPage)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="analytics-empty-state">
              No user activity data yet. User data will appear as people use the application.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnalyticsDashboard;
